import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import {
  BookOpenIcon,
  ArrowRightIcon,
  ChatBubbleLeftRightIcon,
  BoltIcon,
  CheckCircleIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import ReadingActivityHeatmap from '../components/dashboard/ReadingActivityHeatmap';
import ReadingStats from '../components/dashboard/ReadingStats';
import { useIsMobile } from '../hooks/useIsMobile';
import { MobileReaderModal } from '../components/common/MobileReaderModal';

interface DailyActivity {
  date: string;
  minutes: number;
}

interface ReadingActivity {
  activity: DailyActivity[];
  current_streak: number;
  longest_streak: number;
  total_days_read: number;
  total_minutes: number;
}


// ── Dashboard Pulse Check Overlay — glass panel for reviewing concepts ──
const DashboardPulseOverlay: React.FC<{
  concepts: any[];
  startIndex: number;
  isDarkMode: boolean;
  onDismiss: () => void;
}> = ({ concepts, startIndex, isDarkMode, onDismiss }) => {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [revealed, setRevealed] = useState(false);
  const [reviewedSet, setReviewedSet] = useState<Set<number>>(new Set());
  const reviewedRef = React.useRef<Set<number>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const current = concepts[currentIndex];
  const reviewedCount = reviewedSet.size;

  // Reset state on index change
  useEffect(() => { setRevealed(false); }, [currentIndex]);

  const handleRate = async (rating: 'got_it' | 'fuzzy' | 'forgot') => {
    if (!current?.concept_memory_id) return;
    setSubmitting(true);
    try {
      await api.post('/api/pulse/review', {
        concept_memory_id: current.concept_memory_id,
        rating,
      });
      const newReviewed = new Set(reviewedRef.current).add(currentIndex);
      reviewedRef.current = newReviewed;
      setReviewedSet(newReviewed);
    } catch (err) {
      console.error('Dashboard review failed:', err);
    } finally {
      setSubmitting(false);
      // Auto-advance to next unreviewed (use ref for latest state)
      for (let i = 1; i < concepts.length; i++) {
        const next = (currentIndex + i) % concepts.length;
        if (!reviewedRef.current.has(next)) {
          setCurrentIndex(next);
          return;
        }
      }
      // All reviewed — dismiss and refresh
      setTimeout(onDismiss, 500);
    }
  };

  const goTo = (i: number) => {
    if (i >= 0 && i < concepts.length) setCurrentIndex(i);
  };

  const getDotStyle = (i: number) => {
    if (i === currentIndex) return 'w-3 h-3 ring-2 ring-cyan-400/40 bg-cyan-500';
    if (reviewedSet.has(i)) return 'w-2 h-2 bg-emerald-500';
    return isDarkMode ? 'w-2 h-2 bg-slate-600' : 'w-2 h-2 bg-slate-300';
  };

  const gradientBtn = { background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center"
      style={{
        backdropFilter: 'blur(24px) saturate(1.4)',
        WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
        background: isDarkMode ? 'rgba(3, 7, 18, 0.88)' : 'rgba(250, 251, 252, 0.92)',
      }}
    >
      {/* Header */}
      <div className="absolute top-5 left-6 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={gradientBtn}>
          <BoltIcon className={`h-4 w-4 text-white`} />
        </div>
        <div>
          <h2 className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Mnemo</h2>
          <p className={`text-[11px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            {reviewedCount} of {concepts.length} reviewed
          </p>
        </div>
      </div>

      {/* Close */}
      <button onClick={onDismiss}
        className={`absolute top-5 right-6 p-2 rounded-xl transition ${isDarkMode ? 'hover:bg-white/5 text-slate-500' : 'hover:bg-black/5 text-slate-400'}`}>
        <XMarkIcon className="h-5 w-5" />
      </button>

      {/* Progress dots */}
      <div className="absolute top-16 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
        <button onClick={() => goTo(currentIndex - 1)} disabled={currentIndex === 0}
          className={`p-1 rounded-lg ${currentIndex === 0 ? 'opacity-20' : isDarkMode ? 'hover:bg-white/5' : 'hover:bg-black/5'}`}>
          <ChevronLeftIcon className={`h-3.5 w-3.5 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
        </button>
        {concepts.map((_: any, i: number) => (
          <button key={i} onClick={() => goTo(i)}
            className={`rounded-full transition-all duration-300 ${getDotStyle(i)}`}
          />
        ))}
        <button onClick={() => goTo(currentIndex + 1)} disabled={currentIndex === concepts.length - 1}
          className={`p-1 rounded-lg ${currentIndex === concepts.length - 1 ? 'opacity-20' : isDarkMode ? 'hover:bg-white/5' : 'hover:bg-black/5'}`}>
          <ChevronRightIcon className={`h-3.5 w-3.5 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
        </button>
      </div>

      {/* Content */}
      {current && (
        <div className="max-w-md w-full px-6">
          {/* Source */}
          <p className={`text-[10px] font-semibold uppercase tracking-widest mb-2 text-amber-400`}>
            Review · {current.days_overdue > 0 ? `${current.days_overdue} day${current.days_overdue !== 1 ? 's' : ''} overdue` : 'due now'}
          </p>

          {/* Concept name */}
          <h3 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {current.concept}
          </h3>

          {reviewedSet.has(currentIndex) ? (
            // Already reviewed
            <div className="flex items-center gap-2 mb-4">
              <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
              <span className={`text-sm ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>Reviewed</span>
            </div>
          ) : !revealed ? (
            // Not revealed yet
            <div className="space-y-4">
              <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Think about what this means, then reveal the answer
              </p>
              <button onClick={() => setRevealed(true)}
                className={`w-full py-3 text-sm font-medium rounded-xl border transition-all ${
                  isDarkMode ? 'border-white/10 text-slate-300 hover:bg-white/5' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}>
                Reveal Answer
              </button>
            </div>
          ) : (
            // Revealed — show definition + rating
            <div className="space-y-4">
              <div className={`p-5 rounded-xl ${
                isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'
              }`}>
                <p className={`text-[10px] font-semibold uppercase tracking-widest mb-2 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                  Definition
                </p>
                <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  {current.definition || `Think about what "${current.concept}" means — rate your confidence below.`}
                </p>
              </div>

              <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                How well did you know this?
              </p>

              <div className="grid grid-cols-3 gap-3">
                <button onClick={() => handleRate('got_it')} disabled={submitting}
                  className="py-3 text-sm font-semibold rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white transition-all hover:shadow-lg hover:shadow-emerald-500/20 active:scale-[0.97] disabled:opacity-50">
                  Got it
                </button>
                <button onClick={() => handleRate('fuzzy')} disabled={submitting}
                  className="py-3 text-sm font-semibold rounded-xl bg-amber-500 hover:bg-amber-600 text-white transition-all hover:shadow-lg hover:shadow-amber-500/20 active:scale-[0.97] disabled:opacity-50">
                  Fuzzy
                </button>
                <button onClick={() => handleRate('forgot')} disabled={submitting}
                  className="py-3 text-sm font-semibold rounded-xl bg-red-500 hover:bg-red-600 text-white transition-all hover:shadow-lg hover:shadow-red-500/20 active:scale-[0.97] disabled:opacity-50">
                  Forgot
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Dashboard: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();

  // Time-of-day greeting. Uses the browser's local clock so the phrasing
  // matches the user's day, regardless of where Vidya is hosted.
  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  })();
  const firstName = user?.name ? user.name.split(' ')[0] : null;
  const { isMobile } = useIsMobile();
  const [books, setBooks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activityData, setActivityData] = useState<ReadingActivity | null>(null);
  const [activityLoading, setActivityLoading] = useState(true);
  const [chatStats, setChatStats] = useState<{ total_questions: number } | null>(null);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [selectedBookTitle, setSelectedBookTitle] = useState<string | null>(null);
  const [pulseData, setPulseData] = useState<any>(null);
  const [pulseLoading, setPulseLoading] = useState(true);
  const [dashPulseOpen, setDashPulseOpen] = useState(false);
  const [dashPulseStartIndex, setDashPulseStartIndex] = useState(0);

  // Fetch books on mount
  useEffect(() => {
    const fetchBooks = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/books/');
        setBooks(response.data);
      } catch (error) {
        console.error('Failed to fetch books:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBooks();
  }, []);

  // Fetch reading activity on mount
  useEffect(() => {
    const fetchActivity = async () => {
      try {
        setActivityLoading(true);
        // Pass user's timezone so sessions are grouped by local date, not UTC
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const response = await api.get(`/api/users/me/reading-activity?user_timezone=${encodeURIComponent(timezone)}`);
        setActivityData(response.data);
      } catch (error) {
        console.error('Failed to fetch reading activity:', error);
      } finally {
        setActivityLoading(false);
      }
    };
    fetchActivity();
  }, []);

  // Fetch chat stats on mount
  useEffect(() => {
    const fetchChatStats = async () => {
      try {
        const response = await api.get('/api/chat/stats');
        setChatStats(response.data);
      } catch (error) {
        console.error('Failed to fetch chat stats:', error);
      }
    };
    fetchChatStats();
  }, []);

  // Fetch Knowledge Pulse data on mount
  useEffect(() => {
    const fetchPulse = async () => {
      try {
        setPulseLoading(true);
        const response = await api.get('/api/pulse/check');
        setPulseData(response.data);
      } catch (error) {
        console.error('Failed to fetch pulse data:', error);
      } finally {
        setPulseLoading(false);
      }
    };
    fetchPulse();
  }, []);

  // Calculate real stats from data
  const completedBooks = books.filter(b => b.progress_percentage === 100).length;
  const inProgressBooks = books.filter(b => b.progress_percentage > 0 && b.progress_percentage < 100).length;
  const totalPagesRead = books.reduce((sum, book) => sum + (book.max_page_reached || 0), 0);

  // Show Doubts Resolved if any, otherwise Pages Read
  const getFirstStat = () => {
    if (chatStats && chatStats.total_questions > 0) {
      return { name: 'Doubts Resolved', value: chatStats.total_questions.toString(), icon: ChatBubbleLeftRightIcon, color: 'cyan' };
    }
    return { name: 'Pages Read', value: totalPagesRead.toString(), icon: BookOpenIcon, color: 'cyan' };
  };

  const stats = [
    getFirstStat(),
    { name: 'Currently Reading', value: inProgressBooks.toString(), icon: BookOpenIcon, color: 'emerald' },
  ];

  // Get recently read books (with progress, sorted by last_read_at)
  const recentBooks = books
    .filter(book => book.progress_percentage > 0 && book.progress_percentage < 100)
    .sort((a, b) => {
      const dateA = a.last_read_at ? new Date(a.last_read_at).getTime() : 0;
      const dateB = b.last_read_at ? new Date(b.last_read_at).getTime() : 0;
      return dateB - dateA;
    })
    .slice(0, 3);

  const getColorClasses = (color: string, type: 'bg' | 'text' | 'border') => {
    const colorMap: Record<string, Record<string, string>> = {
      cyan: {
        bg: isDarkMode ? 'bg-cyan-500/10' : 'bg-cyan-50',
        text: isDarkMode ? 'text-cyan-400' : 'text-cyan-600',
        border: isDarkMode ? 'border-cyan-500/20' : 'border-cyan-200',
      },
      emerald: {
        bg: isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-50',
        text: isDarkMode ? 'text-emerald-400' : 'text-emerald-600',
        border: isDarkMode ? 'border-emerald-500/20' : 'border-emerald-200',
      },
    };
    return colorMap[color][type];
  };

  return (
    <Layout>
      <div className="p-4 md:p-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className={`text-2xl md:text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {greeting}{firstName ? `, ${firstName}` : ''}. 👋
          </h1>
          <p className={`mt-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
            {tenant
              ? `Welcome to ${tenant.name} — ready to continue your learning journey?`
              : 'Ready to continue your learning journey?'}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {loading ? (
            <>
              <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-slate-900/50 border border-slate-700/50' : 'bg-white border border-gray-200'}`}>
                <div className="animate-pulse flex items-center justify-between">
                  <div className="flex-1">
                    <div className={`h-4 rounded w-24 mb-2 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
                    <div className={`h-8 rounded w-16 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
                  </div>
                  <div className={`h-12 w-12 rounded-xl ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
                </div>
              </div>
              <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-slate-900/50 border border-slate-700/50' : 'bg-white border border-gray-200'}`}>
                <div className="animate-pulse flex items-center justify-between">
                  <div className="flex-1">
                    <div className={`h-4 rounded w-24 mb-2 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
                    <div className={`h-8 rounded w-16 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
                  </div>
                  <div className={`h-12 w-12 rounded-xl ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
                </div>
              </div>
            </>
          ) : (
            stats.map((stat) => (
              <div
                key={stat.name}
                className={`rounded-2xl p-6 transition-all ${
                  isDarkMode
                    ? 'bg-slate-900/50 border border-slate-700/50 hover:border-slate-600'
                    : 'bg-white border border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                      {stat.name}
                    </p>
                    <p className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {stat.value}
                    </p>
                  </div>
                  <div className={`p-3 rounded-xl ${getColorClasses(stat.color, 'bg')}`}>
                    <stat.icon className={`h-6 w-6 ${getColorClasses(stat.color, 'text')}`} />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Reading Activity Section */}
        <div className="mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3">
              <ReadingActivityHeatmap
                activity={activityData?.activity || []}
                loading={activityLoading}
              />
            </div>
            <div className="lg:col-span-1">
              <ReadingStats
                currentStreak={activityData?.current_streak || 0}
                longestStreak={activityData?.longest_streak || 0}
                totalDaysRead={activityData?.total_days_read || 0}
                totalMinutes={activityData?.total_minutes || 0}
                loading={activityLoading}
              />
            </div>
          </div>
        </div>

        {/* Mnemo — concepts blur, crack, and desaturate as memory decays. Hover heals. */}
        <style>{`
          .recall-card:hover .recall-concept {
            opacity: 1 !important;
            filter: blur(0px) !important;
          }
          .recall-card:hover .recall-card-inner {
            filter: saturate(1) !important;
          }
          .recall-card:hover .recall-cracks {
            opacity: 0 !important;
          }
        `}</style>
        {pulseLoading && (
          <div className="mb-8">
            <div className="mb-5">
              <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Mnemo : Your Memory, Managed
              </h2>
              <p className="group/tag relative h-5 mt-1 cursor-pointer w-fit">
                <span className={`text-xs italic tracking-wide underline decoration-dotted underline-offset-4 transition-all duration-300 group-hover/tag:opacity-0 ${isDarkMode ? 'text-slate-400 decoration-slate-600' : 'text-slate-500 decoration-slate-300'}`}>know it. stow it. mnemo it.</span>
                <span className={`absolute left-0 top-0 text-xs italic tracking-wide transition-all duration-300 opacity-0 group-hover/tag:opacity-100 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>recall. retain. repeat.</span>
              </p>
              {/* Phase 2 multilingual: signal that a translation is in flight. */}
              {user?.language && user.language !== 'english' && (
                <p className={`mt-2 text-xs ${isDarkMode ? 'text-cyan-400/80' : 'text-cyan-600/90'}`}>
                  Translating to {({ hindi: 'हिन्दी', gujarati: 'ગુજરાતી', marathi: 'मराठी' } as Record<string,string>)[user.language] ?? user.language}…
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className={`rounded-2xl h-[140px] animate-pulse ${isDarkMode ? 'bg-slate-800/50' : 'bg-gray-100'}`} />
              ))}
            </div>
          </div>
        )}

        {/* Mnemo — empty state for new users */}
        {!pulseLoading && (!pulseData || !pulseData.fading || pulseData.fading.total_count === 0) && (
          <div className="mb-8">
            <div className={`rounded-2xl p-6 border ${
              isDarkMode
                ? 'bg-slate-800/40 border-slate-700/40'
                : 'bg-white border-gray-200'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}>
                  <BoltIcon className="h-4 w-4 text-white" />
                </div>
                <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Mnemo : Your Memory, Managed
                </h2>
              </div>
              <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                You read it. Mnemo makes sure you <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>actually</span> remember it.
                <span className={`block mt-1.5 text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  Open any book to get started — your first Mnemo check drops after a few pages.
                </span>
              </p>
            </div>
          </div>
        )}

        {!pulseLoading && pulseData && pulseData.fading?.total_count > 0 && (
          <div className="mb-8">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Mnemo : Your Memory, Managed
                </h2>
                <p className="group/tag relative h-5 mt-1 cursor-pointer w-fit">
                  <span className={`text-xs italic tracking-wide underline decoration-dotted underline-offset-4 transition-all duration-300 group-hover/tag:opacity-0 ${isDarkMode ? 'text-slate-400 decoration-slate-600' : 'text-slate-500 decoration-slate-300'}`}>know it. stow it. mnemo it.</span>
                  <span className={`absolute left-0 top-0 text-xs italic tracking-wide transition-all duration-300 opacity-0 group-hover/tag:opacity-100 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>recall. retain. repeat.</span>
                </p>
              </div>
              {pulseData.stats && (
                <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                  {pulseData.stats.mastery_percentage}% mastered
                </span>
              )}
            </div>

            {/* Concept cards — text blurs + fades as memory decays */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {(pulseData.fading?.pulse_check || []).map((concept: any, idx: number) => {
                const d = concept.days_overdue;

                // Memory decay: text blurs + fades. You can't read it → you're forgetting it.
                const blurPx = Math.min(2.5, d * 0.6);       // 0→0, 1→0.6, 2→1.2, 3→1.8, 5→2.5
                const textOpacity = Math.max(0.22, 1 - d * 0.18); // 0→1, 1→0.82, 2→0.64, 3→0.46, 5→0.22
                const cardSaturation = Math.max(0.3, 1 - d * 0.15); // card desaturates like Duolingo

                const overdueColor = d >= 3 ? (isDarkMode ? 'text-red-400' : 'text-red-500')
                  : d >= 1 ? (isDarkMode ? 'text-amber-400' : 'text-amber-600')
                  : (isDarkMode ? 'text-cyan-400' : 'text-cyan-600');
                const overdueText = d === 0 ? 'due today'
                  : d === 1 ? '1 day overdue'
                  : `${d} days overdue`;

                // Domain-based card color — each subject gets its own tint
                const domainColors: Record<string, { dark: string; light: string; border: string; labelDark: string; labelLight: string }> = {
                  'Physics':     { dark: 'from-blue-500/25 to-blue-900/15',      light: 'from-blue-50 to-blue-100/60',    border: 'border-blue-500/30',    labelDark: 'text-blue-300',    labelLight: 'text-blue-600' },
                  'Chemistry':   { dark: 'from-emerald-500/25 to-emerald-900/15', light: 'from-emerald-50 to-emerald-100/60', border: 'border-emerald-500/30', labelDark: 'text-emerald-300', labelLight: 'text-emerald-600' },
                  'Mathematics': { dark: 'from-violet-500/25 to-violet-900/15', light: 'from-violet-50 to-violet-100/60', border: 'border-violet-500/30', labelDark: 'text-violet-300',  labelLight: 'text-violet-600' },
                  'Biology':     { dark: 'from-green-500/25 to-green-900/15',   light: 'from-green-50 to-green-100/60',   border: 'border-green-500/30',  labelDark: 'text-green-300',   labelLight: 'text-green-600' },
                  'CFA':         { dark: 'from-amber-500/25 to-amber-900/15',   light: 'from-amber-50 to-amber-100/60',   border: 'border-amber-500/30',  labelDark: 'text-amber-300',   labelLight: 'text-amber-600' },
                  'Finance':     { dark: 'from-amber-500/25 to-amber-900/15',   light: 'from-amber-50 to-amber-100/60',   border: 'border-amber-500/30',  labelDark: 'text-amber-300',   labelLight: 'text-amber-600' },
                };
                const dc = domainColors[concept.domain] || { dark: 'from-slate-500/20 to-slate-800/12', light: 'from-slate-50 to-slate-100/60', border: 'border-slate-500/25', labelDark: 'text-slate-300', labelLight: 'text-slate-500' };

                return (
                  <div key={`fading-${idx}`}
                    onClick={() => { setDashPulseStartIndex(idx); setDashPulseOpen(true); }}
                    className={`recall-card rounded-2xl h-[140px] cursor-pointer transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl active:scale-[0.97] ${
                      isDarkMode
                        ? 'hover:shadow-cyan-500/10'
                        : 'hover:shadow-gray-300/60'
                    }`}
                  >
                    <div
                      className={`recall-card-inner relative rounded-2xl h-full p-4 flex flex-col justify-between transition-[filter] duration-400 overflow-hidden bg-gradient-to-br border ${
                        isDarkMode
                          ? `${dc.dark} ${dc.border} hover:border-cyan-500/30`
                          : `${dc.light} ${dc.border} hover:border-cyan-400/40`
                      }`}
                      style={{ filter: `saturate(${cardSaturation})` }}
                    >
                      {/* Crack overlay — progressive fractures as memory breaks down */}
                      <svg
                        className="recall-cracks absolute inset-0 w-full h-full pointer-events-none transition-opacity duration-500"
                        viewBox="0 0 200 140"
                        preserveAspectRatio="none"
                        fill="none"
                        style={{ opacity: Math.min(1, 0.25 + d * 0.18) }}
                      >
                        {/* Crack glow — faint light bleeding through fractures */}
                        <g stroke={isDarkMode ? 'rgba(239,68,68,0.10)' : 'rgba(239,68,68,0.06)'} strokeWidth="5" strokeLinecap="round">
                          <path d="M155,0 L150,18 L158,32 L148,50 L155,68 L146,88" />
                          {d >= 2 && <path d="M0,108 L22,100 L42,110 L65,98 L82,106" />}
                          {d >= 3 && <path d="M40,0 L45,22 L37,40 L46,56" />}
                        </g>

                        {/* Primary crack — hairline at due today, grows with overdue */}
                        <path
                          d="M155,0 L150,18 L158,32 L148,50 L155,68 L146,88"
                          stroke={isDarkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.12)'}
                          strokeWidth={d === 0 ? '0.7' : '1.2'}
                          strokeLinecap="round"
                        />

                          {/* 2+ days: branch crack + bottom-left fracture */}
                          {d >= 2 && <>
                            <path
                              d="M148,50 L134,56 L118,50 L105,58"
                              stroke={isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.10)'}
                              strokeWidth="1"
                              strokeLinecap="round"
                            />
                            <path
                              d="M0,108 L22,100 L42,110 L65,98 L82,106"
                              stroke={isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.09)'}
                              strokeWidth="1.1"
                              strokeLinecap="round"
                            />
                          </>}

                          {/* 3+ days: spreading fracture network */}
                          {d >= 3 && <>
                            <path
                              d="M40,0 L45,22 L37,40 L46,56"
                              stroke={isDarkMode ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.10)'}
                              strokeWidth="1.1"
                              strokeLinecap="round"
                            />
                            <path
                              d="M200,42 L178,48 L162,40 L148,50"
                              stroke={isDarkMode ? 'rgba(255,255,255,0.13)' : 'rgba(0,0,0,0.08)'}
                              strokeWidth="1"
                              strokeLinecap="round"
                            />
                            <path
                              d="M65,98 L78,82 L95,88"
                              stroke={isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.07)'}
                              strokeWidth="0.9"
                              strokeLinecap="round"
                            />
                          </>}

                          {/* 4+ days: dense shattering + small fragment chips */}
                          {d >= 4 && <>
                            <path
                              d="M105,58 L98,72 L108,85 L96,100"
                              stroke={isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.09)'}
                              strokeWidth="1"
                              strokeLinecap="round"
                            />
                            <path
                              d="M46,56 L60,62 L78,54 L95,62"
                              stroke={isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}
                              strokeWidth="0.9"
                              strokeLinecap="round"
                            />
                            {/* Fragment chips at crack intersections */}
                            <polygon
                              points="146,48 152,44 150,52"
                              fill={isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}
                            />
                            <polygon
                              points="63,96 70,92 66,100"
                              fill={isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'}
                            />
                          </>}
                        </svg>

                      {/* Concept name — blurs as you forget, sharpens on hover */}
                      <p
                        className={`recall-concept relative z-10 text-[15px] font-bold leading-snug line-clamp-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}
                        style={{
                          opacity: textOpacity,
                          filter: `blur(${blurPx}px)`,
                          transition: 'opacity 0.4s ease, filter 0.4s ease',
                        }}
                      >
                        {concept.concept}
                      </p>

                      {/* Bottom row: domain + overdue — always crisp, above cracks */}
                      <div className="relative z-10 flex items-end justify-between gap-2">
                        <span className={`text-[11px] font-semibold tracking-wide ${isDarkMode ? dc.labelDark : dc.labelLight}`}>
                          {concept.domain || ''}
                        </span>
                        <span className={`text-[11px] font-medium whitespace-nowrap ${overdueColor}`}>
                          {overdueText}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Recent Books */}
          <div className="lg:col-span-2">
            <div className={`rounded-2xl p-6 ${
              isDarkMode 
                ? 'bg-slate-900/50 border border-slate-700/50' 
                : 'bg-white border border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Continue Reading
                </h2>
                <Link 
                  to="/library" 
                  className={`text-sm font-medium transition-colors ${
                    isDarkMode ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-600 hover:text-cyan-700'
                  }`}
                >
                  View Library →
                </Link>
              </div>
              <div className="space-y-4">
                {loading ? (
                  // Loading skeleton
                  <>
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`rounded-xl p-4 ${
                          isDarkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-gray-50 border border-gray-200'
                        }`}
                      >
                        <div className="animate-pulse">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex-1">
                              <div className={`h-4 rounded w-3/4 mb-2 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-300'}`}></div>
                              <div className={`h-3 rounded w-1/2 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-300'}`}></div>
                            </div>
                            <div className={`h-4 rounded w-12 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-300'}`}></div>
                          </div>
                          <div className={`w-full rounded-full h-2 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
                        </div>
                      </div>
                    ))}
                  </>
                ) : recentBooks.length > 0 ? (
                  recentBooks.map((book) => {
                    // Find curiosity hook for this book (from Knowledge Pulse data)
                    const hookPrompt = (pulseData?.curiosity_prompts || []).find(
                      (p: any) => p.book_id === book.id?.toString()
                    );
                    return (
                    <div
                      key={book.id}
                      onClick={() => {
                        if (isMobile) {
                          setSelectedBookTitle(book.title);
                          setShowMobileModal(true);
                        } else {
                          navigate(`/dhyana/${book.id}`);
                        }
                      }}
                      className={`rounded-xl p-4 transition-all cursor-pointer ${
                        isDarkMode
                          ? 'bg-slate-800/50 hover:bg-slate-800 border border-slate-700'
                          : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {book.title}
                      </h3>
                      <p className={`text-sm mt-1 leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                        {hookPrompt?.hook_text || book.author}
                      </p>
                    </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <BookOpenIcon className={`h-12 w-12 mx-auto mb-3 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                      No books in progress yet
                    </p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                      Start reading from your library
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="space-y-6">
            {/* Ask Vidya */}
            <div className={`rounded-2xl p-6 border transition-all ${
              isDarkMode
                ? 'bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border-cyan-500/20 hover:border-cyan-500/30'
                : 'bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200 hover:border-cyan-300'
            }`}>
              <div className="flex items-center mb-4">
                <div 
                  className="p-3 rounded-xl"
                  style={{ 
                    background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                    boxShadow: '0 0 20px rgba(6, 182, 212, 0.3)',
                  }}
                >
                  <ChatBubbleLeftRightIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className={`text-lg font-bold ml-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Ask Vidya
                </h3>
              </div>
              <p className={`mb-4 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                Your AI study companion. Get explanations, clarify concepts, and deepen your understanding.
              </p>
              <Link
                to="/chat"
                className="inline-flex items-center px-4 py-2.5 text-white rounded-xl transition-all font-medium"
                style={{ 
                  background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                  boxShadow: '0 0 20px rgba(6, 182, 212, 0.25)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 0 30px rgba(6, 182, 212, 0.4)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(6, 182, 212, 0.25)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Start Learning
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

      </div>

      {/* Mobile Reader Modal */}
      <MobileReaderModal
        isOpen={showMobileModal}
        onClose={() => setShowMobileModal(false)}
        bookTitle={selectedBookTitle || undefined}
      />

      {/* Dashboard Pulse Check — glass overlay for reviewing concepts */}
      {dashPulseOpen && pulseData?.fading?.pulse_check && (
        <DashboardPulseOverlay
          concepts={pulseData.fading.pulse_check}
          startIndex={dashPulseStartIndex}
          isDarkMode={isDarkMode}
          onDismiss={() => {
            setDashPulseOpen(false);
            // Show loading state while refreshing pulse data
            setPulseLoading(true);
            (async () => {
              try {
                const res = await api.get('/api/pulse/check');
                setPulseData(res.data);
              } catch {}
              finally { setPulseLoading(false); }
            })();
          }}
        />
      )}
    </Layout>
  );
};

export default Dashboard;
